import { Component, inject, input, output, ChangeDetectionStrategy, computed } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { formatDate } from '@angular/common';
import { DataDisplayComponent } from 'htx-ui-library';

@Component({
  selector: 'app-registration-step-4-summary',
  imports: [TranslocoDirective, DataDisplayComponent],
  templateUrl: './registration-step-4-summary.component.html',
  styleUrl: './registration-step-4-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegistrationStep4SummaryComponent {
  private translocoService = inject(TranslocoService);

  readonly userForm = input.required<FormGroup>();
  readonly organisationForm = input.required<FormGroup>();
  readonly verificationForm = input.required<FormGroup>();
  readonly editStep = output<number>();

  userDetails = computed(() => {
    const userVal = this.userForm().value;
    const translatedSalutation = userVal.salutation
      ? this.translocoService.translate(
          `registration.page.userForm.controls.salutation.options.${userVal.salutation}`
        )
      : '';
    return [
      {
        label: this.translocoService.translate('registration.page.summary.name.label'),
        value: [
          [translatedSalutation, userVal.firstName, userVal.lastName].filter(Boolean).join(' ')
        ]
      },
      {
        label: this.translocoService.translate('registration.page.summary.contactData.label'),
        value: [
          this.translocoService.translate(
            'registration.page.summary.contactData.phoneNumber.label'
          ) + ` ${userVal.phoneNumber}`,
          this.translocoService.translate('registration.page.summary.contactData.email.label') +
            ` ${userVal.email}`
        ]
      },
      {
        label: this.translocoService.translate('registration.page.summary.position.label'),
        value: [userVal.position]
      }
    ];
  });

  organisationDetails = computed(() => {
    const orgVal = this.organisationForm().value;
    const t = (key: string) => this.translocoService.translate(`registration.page.summary.${key}`);
    return [
      { label: t('legalName.label'), value: [orgVal.legalName] },
      { label: t('shortName.label'), value: [orgVal.shortName] },
      { label: t('legalForm.label'), value: [orgVal.legalForm] },
      {
        label: t('address.label'),
        value: [
          `${orgVal.street ?? ''} ${orgVal.houseNumber ?? ''}`.trim(),
          `${orgVal.postalCode ?? ''} ${orgVal.city ?? ''}`.trim()
        ]
      },
      { label: t('website.label'), value: [orgVal.website] },
      { label: t('sector.label'), value: [orgVal.sector] },
      { label: t('descriptionField.label'), value: [orgVal.description] }
    ];
  });

  organisationVerificationDetails = computed(() => {
    const verVal = this.verificationForm().value;
    const t = (key: string) => this.translocoService.translate(`registration.page.summary.${key}`);
    return [
      {
        label: t('foundingDate.label'),
        value: [verVal.foundingDate ? formatDate(verVal.foundingDate, 'dd.MM.yyyy', 'de-DE') : '']
      },
      {
        label: t('commercialRegisterNumber.label'),
        value: [verVal.commercialRegisterNumber, verVal.commercialRegisterCourt]
      },
      { label: t('taxIdNumber.label'), value: [verVal.taxIdNumber] }
    ];
  });
}
